
import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

interface TestQuizProps {
  onComplete: (answers: Record<number, string | string[]>) => void;
}

const TestQuiz = ({ onComplete }: TestQuizProps) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [answers, setAnswers] = useState<Record<number, string | string[]>>({});

  const steps = [
    {
      id: 1,
      title: '主体适配评估',
      subtitle: '谁在做出口',
      description: '判断是否具备1039合法申报主体路径',
      questions: [
        {
          id: 1,
          question: '您当前的申报主体情况是？',
          type: 'single',
          options: [
            { value: '已有市场内个体工商户', label: '已有市场内个体工商户', status: 'green', tip: '✓ 可直接进入' },
            { value: '有公司但无个体户', label: '有公司，但无个体户', status: 'yellow', tip: '⚠ 需补充步骤' },
            { value: '暂无任何主体', label: '暂无任何主体', status: 'yellow', tip: '⚠ 可协助注册' },
          ],
        }
      ]
    },
    {
      id: 2,
      title: '商品与交易结构',
      subtitle: '能不能报',
      description: '初步排除禁限商品，判断是否符合"多品类、小批量"特征',
      questions: [
        {
          id: 2,
          question: '您的主要出口商品大类？（可多选）',
          type: 'multiple',
          options: [
            { value: '服装鞋帽', label: '服装鞋帽', icon: 'ri-shirt-line' },
            { value: '箱包皮具', label: '箱包皮具', icon: 'ri-handbag-line' },
            { value: '家居用品', label: '家居用品', icon: 'ri-home-4-line' },
            { value: '五金工具', label: '五金工具', icon: 'ri-tools-line' },
            { value: '小电器', label: '小电器', icon: 'ri-plug-line' },
            { value: '其他日用品', label: '其他日用品', icon: 'ri-shopping-bag-line' },
          ],
        },
        {
          id: 3,
          question: '是否涉及国家禁限类商品？',
          type: 'single',
          options: [
            { value: '确定不涉及', label: '确定不涉及', status: 'green' },
            { value: '不确定', label: '不确定', status: 'yellow', tip: '需人工复核' },
            { value: '可能涉及', label: '可能涉及', status: 'red', tip: '⚠ 风险提示' },
          ],
        }
      ]
    },
    {
      id: 3,
      title: '金额与目的国',
      subtitle: '卡点判断',
      description: '判断是否符合1039单票金额限制',
      questions: [
        {
          id: 4,
          question: '您的单票申报金额区间？',
          type: 'single',
          options: [
            { value: '≤5万美元', label: '≤5万美元', status: 'green' },
            { value: '5-10万美元', label: '5-10万美元', status: 'green' },
            { value: '10-15万美元', label: '10-15万美元', status: 'green' },
            { value: '>15万美元', label: '>15万美元', status: 'red', tip: '❌ 1039不适用' },
          ],
        },
        {
          id: 5,
          question: '主要出口国家/地区？（可多选）',
          type: 'multiple',
          options: [
            { value: '东南亚', label: '东南亚', icon: 'ri-map-pin-line' },
            { value: '中东', label: '中东', icon: 'ri-map-pin-line' },
            { value: '非洲', label: '非洲', icon: 'ri-map-pin-line' },
            { value: '欧洲', label: '欧洲', icon: 'ri-map-pin-line' },
            { value: '美洲', label: '美洲', icon: 'ri-map-pin-line' },
            { value: '其他', label: '其他', icon: 'ri-map-pin-line' },
          ],
        }
      ]
    },
    {
      id: 4,
      title: '物流与监管条件',
      subtitle: '成单关键',
      description: '识别最大现实落地障碍',
      questions: [
        {
          id: 6,
          question: '是否已有合作物流商？',
          type: 'single',
          options: [
            { value: '有且支持1039', label: '有，且支持1039', status: 'green' },
            { value: '有但不确定支持', label: '有，但不确定是否支持', status: 'yellow' },
            { value: '有但不支持', label: '有，但不支持1039', status: 'yellow', tip: '可对接平台资源' },
            { value: '暂无合作物流商', label: '暂无合作物流商', status: 'yellow', tip: '可对接平台资源' },
          ],
        },
        {
          id: 7,
          question: '是否接受进入监管仓操作？',
          type: 'single',
          options: [
            { value: '完全接受', label: '完全接受', status: 'green' },
            { value: '可以接受', label: '可以接受', status: 'green' },
            { value: '不太愿意', label: '不太愿意', status: 'yellow', tip: '需了解监管仓优势' },
            { value: '不能接受', label: '不能接受', status: 'red', tip: '1039必须通过监管仓' },
          ],
        }
      ]
    }
  ];

  const handleAnswer = (questionId: number, answer: string) => {
    const currentQuestion = getCurrentQuestions().find(q => q.id === questionId);
    
    if (currentQuestion?.type === 'single') {
      setAnswers({ ...answers, [questionId]: answer });
    } else {
      const currentAnswers = (answers[questionId] as string[]) || [];
      if (currentAnswers.includes(answer)) {
        setAnswers({
          ...answers,
          [questionId]: currentAnswers.filter((a) => a !== answer),
        });
      } else {
        setAnswers({
          ...answers,
          [questionId]: [...currentAnswers, answer],
        });
      }
    }
  };

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      onComplete(answers);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const getCurrentQuestions = () => {
    return steps[currentStep].questions;
  };

  const isStepComplete = () => {
    const questions = getCurrentQuestions();
    return questions.every(question => {
      const answer = answers[question.id];
      if (question.type === 'single') {
        return !!answer;
      } else {
        return Array.isArray(answer) && answer.length > 0;
      }
    });
  };

  const progress = ((currentStep + 1) / steps.length) * 100;
  const currentStepData = steps[currentStep];

  return (
    <section className="py-16 min-h-screen bg-gradient-to-br from-neutral-50 to-white">
      {/* Progress Bar */}
      <div className="fixed top-20 left-0 right-0 h-1 bg-neutral-200 z-50">
        <motion.div
          initial={{ width: 0 }}
          animate={{ width: `${progress}%` }}
          transition={{ duration: 0.3 }}
          className="h-full bg-gradient-to-r from-accent to-accent-dark"
        />
      </div>

      <div className="max-w-5xl mx-auto px-8 pt-16">
        {/* Step Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-center mb-12"
        >
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-2xl bg-gradient-to-br from-primary to-primary-dark text-white font-bold text-2xl mb-6">
            {currentStep + 1}
          </div>
          <h2 className="text-4xl font-bold text-primary mb-3">
            {currentStepData.title}
          </h2>
          <p className="text-xl text-primary/70 mb-2 font-medium">
            {currentStepData.subtitle}
          </p>
          <p className="text-neutral-600 max-w-2xl mx-auto">
            {currentStepData.description}
          </p>
        </motion.div>

        <AnimatePresence mode="wait">
          <motion.div
            key={currentStep}
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -50 }}
            transition={{ duration: 0.3 }}
            className="space-y-12"
          >
            {getCurrentQuestions().map((question, qIndex) => (
              <div key={question.id} className="bg-white rounded-3xl p-10 shadow-xl">
                {/* Question Title */}
                <div className="flex items-start gap-4 mb-8">
                  <div className="w-8 h-8 rounded-full bg-accent/20 text-accent font-bold text-lg flex items-center justify-center flex-shrink-0 mt-1">
                    {qIndex + 1}
                  </div>
                  <h3 className="text-2xl font-bold text-primary leading-tight">
                    {question.question}
                  </h3>
                </div>

                {/* Options */}
                <div className={`grid gap-4 ${question.type === 'multiple' && question.options.length > 4 ? 'grid-cols-2' : 'grid-cols-1'}`}>
                  {question.options.map((option, index) => {
                    const isSelected = question.type === 'single'
                      ? answers[question.id] === option.value
                      : (answers[question.id] as string[] || []).includes(option.value);

                    const statusColor = option.status === 'green' ? 'border-green-300 bg-green-50' :
                                       option.status === 'yellow' ? 'border-amber-300 bg-amber-50' :
                                       option.status === 'red' ? 'border-red-300 bg-red-50' : '';

                    return (
                      <motion.button
                        key={index}
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        onClick={() => handleAnswer(question.id, option.value)}
                        className={`relative p-6 rounded-2xl border-2 transition-all duration-300 flex items-center gap-4 text-left font-medium cursor-pointer ${
                          isSelected
                            ? 'bg-accent border-accent text-primary shadow-lg transform scale-105'
                            : `bg-white border-neutral-300 text-neutral-600 hover:border-accent hover:bg-accent/5 ${statusColor}`
                        }`}
                      >
                        {/* Selection Indicator */}
                        <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                          isSelected ? 'border-primary bg-primary' : 'border-neutral-400'
                        }`}>
                          {isSelected && (
                            <i className="ri-check-line text-white text-sm"></i>
                          )}
                        </div>

                        {/* Icon */}
                        {option.icon && (
                          <i className={`${option.icon} text-2xl ${isSelected ? 'text-primary' : 'text-accent'}`}></i>
                        )}

                        {/* Content */}
                        <div className="flex-1">
                          <div className="font-bold text-lg mb-1">{option.label}</div>
                          {option.tip && (
                            <div className={`text-sm ${
                              option.status === 'green' ? 'text-green-600' :
                              option.status === 'yellow' ? 'text-amber-600' :
                              option.status === 'red' ? 'text-red-600' : 'text-neutral-500'
                            }`}>
                              {option.tip}
                            </div>
                          )}
                        </div>

                        {/* Status Icon */}
                        {option.status && (
                          <div className="flex-shrink-0">
                            {option.status === 'green' && <i className="ri-checkbox-circle-line text-2xl text-green-500"></i>}
                            {option.status === 'yellow' && <i className="ri-error-warning-line text-2xl text-amber-500"></i>}
                            {option.status === 'red' && <i className="ri-close-circle-line text-2xl text-red-500"></i>}
                          </div>
                        )}
                      </motion.button>
                    );
                  })}
                </div>
              </div>
            ))}
          </motion.div>
        </AnimatePresence>

        {/* Navigation */}
        <div className="flex items-center justify-between mt-16 bg-white rounded-2xl p-8 shadow-lg">
          <button
            onClick={handlePrevious}
            disabled={currentStep === 0}
            className={`px-8 py-4 rounded-xl font-semibold transition-all duration-300 flex items-center gap-3 cursor-pointer whitespace-nowrap ${
              currentStep === 0
                ? 'bg-neutral-200 text-neutral-400 cursor-not-allowed'
                : 'bg-neutral-100 text-neutral-600 hover:bg-neutral-200'
            }`}
          >
            <i className="ri-arrow-left-line text-xl"></i>
            上一步
          </button>

          {/* Step Indicators */}
          <div className="flex items-center gap-3">
            {steps.map((_, index) => (
              <div
                key={index}
                className={`w-3 h-3 rounded-full transition-all duration-300 ${
                  index === currentStep
                    ? 'bg-accent scale-125'
                    : index < currentStep
                    ? 'bg-primary'
                    : 'bg-neutral-300'
                }`}
              />
            ))}
          </div>

          <button
            onClick={handleNext}
            disabled={!isStepComplete()}
            className={`px-8 py-4 rounded-xl font-semibold transition-all duration-300 flex items-center gap-3 cursor-pointer whitespace-nowrap ${
              isStepComplete()
                ? 'bg-gradient-to-r from-accent to-accent-dark text-primary hover:shadow-lg'
                : 'bg-neutral-200 text-neutral-400 cursor-not-allowed'
            }`}
          >
            {currentStep === steps.length - 1 ? '完成评估' : '下一步'}
            <i className="ri-arrow-right-line text-xl"></i>
          </button>
        </div>

        {/* Risk Notice */}
        <div className="mt-8 bg-amber-50 border border-amber-200 rounded-xl p-6">
          <div className="flex items-start gap-3">
            <i className="ri-information-line text-2xl text-amber-600 mt-1"></i>
            <div>
              <p className="text-sm text-amber-800 font-medium mb-1">
                合规提示
              </p>
              <p className="text-sm text-amber-700">
                此评估基于当前填报信息的初步判断，最终业务合规性以海关总署及相关监管部门要求为准。我们将在结果页面提供详细的风险提示和合规建议。
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default TestQuiz;
